<template>
  <div class="statistics-dashboard">
    <div class="welcome-section" style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:24px;padding:20px;background-color:var(--secondary-bg);border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.05);">
        <div style="font-size:1rem;font-weight:600;color:var(--text-primary);">统计分析</div>
        <div class="welcome-actions">
           <router-link to="/admin" class="action-link">返回管理面板</router-link>
        </div>
    </div>

    <div class="statistics-section">
      <h2>Statistics & Analysis</h2>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Link</th>
            <th>PubDate</th>
            <th>RssNode Id</th>
            <th>Is Selected</th>
            <th>Is Readed</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in selArticles" :key="index">
            <td>{{ item.id }}</td>
            <td>{{ item.title }}</td>
            <td>{{ item.link }}</td>
            <td>{{ item.pubDate }}</td>
            <td>{{ item.rssNodeId }}</td>
            <td>{{ item.isSelected }}</td>
            <td>{{ item.isReaded }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-controls">
        <select v-model="articlesPerPage" @change="changePage(1)" class="page-size-select">
            <option :value="25">25 条/页</option>
            <option :value="50">50 条/页</option>
            <option :value="100">100 条/页</option>
        </select>

        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1 || isLoading"
        >
          上一页
        </button>
        <span>第 {{ currentPage }} 页，共 {{ totalPages }} 页</span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages || isLoading"
        >
          下一页
        </button>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="spinner"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/userStore';
import * as apiService from '@/services/apiService.js';

const router = useRouter();
const authStore = useAuthStore();

// State
const selArticles = ref([]);
const totalArticles = ref(0);
const currentPage = ref(1);
const articlesPerPage = ref(25);
const isLoading = ref(false);

// Computed
const totalPages = computed(() => Math.ceil(totalArticles.value / articlesPerPage.value));

// Methods
const logexit = () => {
    authStore.setlogout();
    router.push('/login');
};

const fetchSelectedArticles = async (page = 1) => {
    try {
        isLoading.value = true;
        if (!authStore.isAuthenticated) {
            logexit();
            return;
        }
        const response = await apiService.GetSeletedArticle(
            (val) => isLoading.value = val, 
            authStore.token, 
            page, 
            articlesPerPage.value
        );
        selArticles.value = response.items;
        totalArticles.value = response.totalItems;
        currentPage.value = response.pageIndex;
    } catch (error) {
        console.error('Error fetching selected articles:', error);
    } finally {
        isLoading.value = false;
    }
};

const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        fetchSelectedArticles(page);
    }
};

// Lifecycle
onMounted(() => {
    if (!authStore.isAuthenticated) {
        logexit();
        return;
    }
    fetchSelectedArticles(1);
});
</script>

<style scoped>
.statistics-dashboard {
  padding: 24px;
  font-size: 0.875rem;
  line-height: 1.6;
  background-color: var(--primary-bg);
  min-height: 100vh;
}

.statistics-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: var(--secondary-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.statistics-section h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--text-primary);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  line-height: 1.6;
  background-color: var(--secondary-bg);
  margin-bottom: 16px;
}

th, td {
  border: 1px solid var(--border-color);
  padding: 12px;
  text-align: left;
}

th {
  font-weight: 600;
  background-color: var(--hover-bg);
  color: var(--text-primary);
}

tr:hover {
  background-color: var(--hover-bg);
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 8px;
}

.pagination-controls button {
  padding: 6px 12px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: var(--text-primary);
}

.pagination-controls button:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

.page-size-select {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--secondary-bg);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.875rem;
}

.page-size-select:focus {
    border-color: var(--accent-color);
    outline: none;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.action-link {
  padding: 6px 12px;
  background-color: var(--accent-color);
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}
.action-link:hover {
  background-color: var(--text-primary);
}
</style>
